import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { FilterType } from 'src/app/shared/models/constants/filter-type';
import { Region } from 'src/app/shared/models/dtos/region/region';
import {
  AdminEvent,
  AdminFilters,
  AdministratorDTO,
} from 'src/app/shared/models/dtos/responses/administrator-dto/administrator-dto';

@Component({
  selector: 'app-admin-list',
  templateUrl: './admin-list.component.html',
  styleUrls: ['./admin-list.component.scss'],
})

/**
 * Admin list
 */
export class AdminListComponent implements OnInit {
  search = new FormControl<string>('', [
    Validators.minLength(3),
    Validators.maxLength(255),
  ]);
  page = 0;
  pageSize = 10;
  admins = [] as any[];
  regions: Region[] = [];
  global = true;
  loading = false;
  pulling = false;
  feedItems: any[] = [];
  mockFeedItems: any[] = AdministratorDTO.MockAdmins();
  labels = {
    name: 'name',
    region: 'region',
    accessLevel: 'access-level',
    error: 'unexpected-error',
  };
  filters: AdminFilters = {
    orderAsc: true,
    global: false,
    regional: false,
    viewOnly: false,
    callCenterAgent: false,
    regions: [],
  };
  call?: Subscription;
  showFilter: boolean = false;
  orderDir: boolean = true;
  chips: ChipContent[] = [
    new ChipContent(undefined, FilterType.GLOBAL, FilterType.GLOBAL, false),
    new ChipContent(undefined, FilterType.REGIONAL, FilterType.REGIONAL, false),
    new ChipContent(
      undefined,
      'callCenterAgent',
      FilterType.CALL_CENTER_AGENT,
      false
    ),
    new ChipContent(undefined, 'viewOnly', FilterType.VIEW_ONLY, false),
  ];

  /**
   * @constructor
   */
  constructor(
    private userService: UserService,
    private translationService: TranslationService,
    private snackBarService: SnackBarService
  ) {}

  /**
   * on init
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);
    this.loading = true;
    this.userService.getRegions().subscribe({
      next: (regions) => {
        this.regions = regions;
        this.showFilter = true;
      },
      error: () => {
        this.loading = false;
        this.snackBarService.error(this.labels.error);
      },
      complete: () => {
        this.getAdmins();
      },
    });
  }

  /**
   * Get admins
   */
  getAdmins() {
    this.loading = true;
    if (this.call) {
      this.call.unsubscribe();
    }
    this.page = 0;
    const params = {
      page: this.page,
      pageSize: this.pageSize,
    };
    this.assignParams(params);
    if (this.search.value) {
      Object.assign(params, { search: this.search.value });
    }
    this.call = this.userService.listAdmins(params).subscribe({
      next: (admins) => {
        this.feedItems = admins;
      },
      error: () => {
        this.loading = false;
        this.snackBarService.error(this.labels.error);
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  /**
   * Fetch more admins and append to list
   */
  async pullAdmins() {
    if (this.pulling) {
      return;
    }

    this.pulling = true;
    this.page++;
    const params = {
      page: this.page,
      pageSize: this.pageSize,
    };

    this.assignParams(params);
    this.userService
      .listAdmins(params)
      .subscribe((admins) => {
        if (admins.length > 0) {
          this.feedItems?.push(...admins);
        } else {
          this.page = this.page - 1;
        }
      })
      .add(() => {
        this.pulling = false;
      });
  }

  /**
   * Updates Filters
   * @param {AdminFilters} filters
   */
  updateFilter(filters: AdminFilters) {
    this.filters = filters;
    this.getAdmins();
  }

  /**
   * Assign filter params to be fetched
   * @param {Object} params
   */
  private assignParams(params: {}) {
    params = Object.assign(params, {
      ...params,
      orderAsc: this.filters.orderAsc,
    });

    if (
      this.filters.global ||
      this.filters.regional ||
      this.filters.callCenterAgent ||
      this.filters.viewOnly
    ) {
      Object.assign(params, this.filters);
    }
    if (this.search.value) {
      Object.assign(params, { search: this.search.value });
    }
    if (this.filters.regions.length !== 0) {
      Object.assign(params, { regions: this.filters.regions });
    }
  }

  /**
   * Reacts to changes in the admin list
   * @param {AdminEvent<AdministratorDTO>} adminEvent
   */
  handleItemChange(adminEvent: AdminEvent<AdministratorDTO>) {
    if (adminEvent.action === 'deleted') {
      this.feedItems = this.feedItems.filter(
        (item: AdministratorDTO) => item.id !== adminEvent.payload.id
      );
    } else {
      this.feedItems = this.feedItems.map((item: AdministratorDTO) =>
        item.id === adminEvent.payload.id
          ? { ...adminEvent.payload, picture: item.picture }
          : item
      );
    }
  }
}
